import Head from "next/head";
import { useRouter } from "next/router";
import useSWR from "swr";

import { useMemo } from "react";

import { orderBy } from "lodash";
import truncate from "lodash/truncate";

import AnnoucementSnippet from "components/AnnouncementSnippet";
import { Loader } from "components/customElements/loader";
import EmptyState from "components/EmptyState";
import { entityUrl } from "components/EntityPageLayout";
import { useApi } from "hooks/useApi";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { dataCloudHost } from "utils/envVariables";

import styles from "./style.module.scss";

type EntityPageNewsListProps = {};

export const datacloudNewsUrl = (name: string) => {
  return `${dataCloudHost}/v3/news/search/${name}`;
};

export const entityNewsUrl = (slug: string) => {
  const url = new ApiEndpoint(`/api/v2/entities/${slug}/entity-news`);
  url.searchParams.append("include", "entity,corporate-attachments");

  return url.href;
};

const EntityPageNewsList = ({}: EntityPageNewsListProps) => {
  const route = useRouter();
  const slug = route.query.slug as string;

  const { data: entity } = useApi(entityUrl(slug));
  const { prettyName: name } = entity || {};

  const { data: datacloudData, isValidating: isDatacloudValidating } = useSWR(
    name ? datacloudNewsUrl(name) : null,
    baseFetcher
  );
  const { data: pageData, isValidating } = useSWR(
    entityNewsUrl(slug),
    baseFetcher
  );

  const isEmpty = useMemo(
    () => !datacloudData?.news?.length && !pageData?.length,
    [datacloudData?.news?.length, pageData?.length]
  );

  const news = useMemo(() => {
    if (isEmpty) {
      return [];
    }

    const entityNewsTransformed = (datacloudData?.news || [])?.map(
      (news: any) => {
        return {
          ...news,
          type: "entity-news",
          content: news.description,
          url: news.link,
          source: "Smartkarma News",
          releaseTime: new Date(news.pubDate).toISOString(),
          entity: pageData?.[0]?.entity,
        };
      }
    );

    return orderBy(
      [...(pageData || []), ...(entityNewsTransformed || [])],
      (item: any) => new Date(item.releaseTime).getTime(),
      "desc"
    );
  }, [datacloudData?.news, isEmpty, pageData]);

  const firstNews = datacloudData?.news?.[0] || pageData?.[0];
  const description = truncate(
    `Latest news from top financial publications on ${name} (${
      entity?.uniqueTicker
    })${firstNews ? ` - ${firstNews.title}` : ""}`,
    {
      length: 155,
      separator: /[/\- ]/,
    }
  );

  return (
    <>
      <Head>
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
      </Head>
      <div className={styles.container}>
        <div className={styles.list}>
          {news?.map?.((news: any) => {
            return <AnnoucementSnippet key={news.id} item={news} />;
          })}
          {isValidating || isDatacloudValidating ? (
            <Loader />
          ) : isEmpty ? (
            <EmptyState type="news" entity={entity} />
          ) : (
            <div className={styles.endOfList}>No more news</div>
          )}
        </div>
      </div>
    </>
  );
};

export default EntityPageNewsList;
